import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { CompanionSplitScreenService } from '@nimbus-library/features/companion-chat/services/companion-split-screen.service';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { MenuEntryType } from '@nimbus-library/features/menu/models/menu-entry.model';
import { MenuEntryViewModel } from '@nimbus-library/features/menu/models/menu-entry.view-model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { MenuActions } from '../../state/menu.actions';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-menu-overflow-item',
  templateUrl: './menu-overflow-item.component.html',
  styleUrls: ['./menu-overflow-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, OverlayModule, LocalizationModule, SharedUIModule],
})
export class MenuOverflowItemComponent implements OnInit {
  @Input() public entries: MenuEntryViewModel[] = [];
  @Input() public active = false;
  @Input() public hasPermissionNotification = false;

  @ViewChild(CdkConnectedOverlay, { static: true }) public connectedOverlay: CdkConnectedOverlay;
  @ViewChild(CdkOverlayOrigin, { static: true }) public origin: CdkOverlayOrigin;

  private mouseMoveSubscription: Subscription;
  private openedByKeyboard = false;
  // CDK only positions the panel on attach; entries rendered later (async filters) can grow it past the viewport.
  private readonly panelResizeObserver = new ResizeObserver(() => this.reposition());
  private readonly half = 0.5;

  public isSplitScreenEnabled$: Observable<boolean>;
  public readonly isOpen$ = new BehaviorSubject<boolean>(false);
  public readonly calloutTop$ = new BehaviorSubject<number>(0);
  public readonly viewportMargin = 8;
  public readonly TranslationKeys = TranslationKeys;
  public readonly Icons = ICONS;
  public readonly MenuEntryType = MenuEntryType;
  public readonly connectedPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'top', offsetX: 0, offsetY: -15 },
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'bottom', offsetX: 0, offsetY: 15 },
  ];

  public get hasUserPreferencesNotification(): boolean {
    return this.hasPermissionNotification && this.entries.some((entry) => entry.renderAs === MenuEntryType.UserPreferences);
  }

  private get originElement(): HTMLElement {
    return this.origin.elementRef.nativeElement as HTMLElement;
  }

  public constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly featureFlippingService: FeatureFlippingService,
    private readonly companionSplitScreenService: CompanionSplitScreenService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  public ngOnInit(): void {
    this.isSplitScreenEnabled$ = this.featureFlippingService.isEnabledInFeatureManagement(Feature.CompanionConversationSplitScreen);
  }

  public open(): void {
    this.isOpen$.next(true);
  }

  public close(): void {
    this.isOpen$.next(false);
  }

  public toggleByKeyboard(event: Event): void {
    event.preventDefault();
    this.openedByKeyboard = !this.isOpen$.value;
    this.isOpen$.next(this.openedByKeyboard);
  }

  public onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
      this.originElement.focus();
    }
  }

  // The panel may be pushed back into the viewport, so the callout is aligned to the origin instead of a fixed panel edge.
  public onPositionChange(): void {
    const { top, bottom } = this.originElement.getBoundingClientRect();
    const panelRect = this.connectedOverlay.overlayRef.overlayElement.getBoundingClientRect();
    this.calloutTop$.next(top + (bottom - top) * this.half - panelRect.top);
  }

  public navigate(entry: MenuEntryViewModel): void {
    this.close();

    if (entry.renderAs === MenuEntryType.UserPreferences) {
      this.router.navigate([entry.path], { queryParamsHandling: 'preserve' });
    } else {
      this.store.dispatch(new MenuActions.Navigate(entry));
    }
  }

  public navigateCompanionSplitScreen(): void {
    this.close();
    this.companionSplitScreenService.activateFromMenu();
  }

  public overlayAttached(): void {
    const overlayElement = this.connectedOverlay.overlayRef.overlayElement;

    if (this.openedByKeyboard) {
      setTimeout(() => overlayElement.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
    }

    this.panelResizeObserver.observe(overlayElement);

    // Same hover behaviour as the regular menu items: stay open while the pointer is on the item or the panel.
    this.mouseMoveSubscription = fromEvent<MouseEvent>(this.document, 'mousemove')
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (!this.contains(this.originElement, event) && !this.contains(overlayElement, event)) {
          this.close();
        }
      });
  }

  public overlayDetached(): void {
    this.panelResizeObserver.disconnect();
    this.mouseMoveSubscription?.unsubscribe();
    this.openedByKeyboard = false;
    this.close();
  }

  // positionChange is not emitted when the same fallback position is re-applied, so the callout is updated explicitly.
  private reposition(): void {
    this.connectedOverlay.overlayRef.updatePosition();
    this.onPositionChange();
  }

  private contains(element: HTMLElement, { clientX, clientY }: MouseEvent): boolean {
    const rect = element.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }
}
